import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadMachineMetricsAlarmHistory } from 'redux/metrics_alarm_history/actions';
import { getMetricsAlarmHistoryData } from 'redux/metrics_alarm_history/selectors';
import { IMetricAlarm } from 'types/interfaces/iMetricAlarm';
import { Column, Order, Table } from 'web-components-ts';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import T from 'prop-types';
import { HeadingWrapper, StyledMarginDiv, StyledPaper } from './Alarms.styled';
import { isValidDateString } from '../../../helpers/utils';
import { defaultEmptyCellValue } from '../../../helpers/stringHandler';

interface AlarmsProps {
  machineId: string;
  from: string;
  to: string;
}

const Alarms = ({ machineId, from, to }: AlarmsProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const rowsPerPage = 5;
  const firstPage = 1;
  const [alarmHistoryList, setAlarmHistoryList] = useState<IMetricAlarm[]>([]);
  const [currentPage, setCurrentPage] = useState(firstPage);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('time');
  const [order, setOrder] = useState('DESC');

  const { data: getMetricsAlarmHistoryResponse } = useSelector(getMetricsAlarmHistoryData);

  let locale = 'en-GB';

  switch (i18n.language) {
    case 'en_US':
      locale = 'en-GB';
      break;
    case 'de_DE':
      locale = 'de';
      break;
    case 'pt_BR':
      locale = 'pt-BR';
      break;
    case 'es_ES':
      locale = 'es';
      break;
    default:
      locale = 'en-GB';
  }

  const formatDate = (date: string) =>
    new Date(date).toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

  const getDateValue = (dateStr: string) => (isValidDateString(dateStr) ? formatDate(dateStr) : defaultEmptyCellValue);

  useEffect(() => {
    dispatch(
      loadMachineMetricsAlarmHistory({
        page: currentPage,
        limit: rowsPerPage,
        sort: sortBy,
        order,
        machine_ids: machineId,
        from,
        to
      })
    );
  }, [dispatch]);

  const columns: Column<IMetricAlarm>[] = [
    {
      field: 'time',
      label: t('batch_records.alarms.time'),
      sort: true,
      render: (row: IMetricAlarm) => <span>{getDateValue(row.time)}</span>
    },
    {
      field: 'resolved',
      label: t('batch_records.alarms.resolved'),
      sort: true,
      render: (row: IMetricAlarm) => <span>{getDateValue(row.resolved)}</span>
    },
    {
      field: 'message',
      label: t('batch_records.alarms.message'),
      sort: false,
      render: (row: IMetricAlarm) => (
        <span>{row.is_custom ? row.message : `${row.code}: ${t(`dashboard.machines.error_codes.${row.code}`)}`}</span>
      )
    }
  ];

  useEffect(() => {
    if (getMetricsAlarmHistoryResponse?.data?.length) {
      setCurrentPage(getMetricsAlarmHistoryResponse.page || 0);
      setTotalPages(getMetricsAlarmHistoryResponse.total_pages || 0);
      setAlarmHistoryList(getMetricsAlarmHistoryResponse.data);
    } else {
      setAlarmHistoryList([]);
    }
  }, [getMetricsAlarmHistoryResponse]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
    dispatch(
      loadMachineMetricsAlarmHistory({
        page,
        order,
        sort: sortBy,
        limit: rowsPerPage,
        machine_ids: machineId,
        from,
        to
      })
    );
  };

  const handleTableSort = (orderParam: Order, orderByParam: keyof IMetricAlarm) => {
    setSortBy(orderByParam);
    setOrder(orderParam.toUpperCase());
    dispatch(
      loadMachineMetricsAlarmHistory({
        order: orderParam.toUpperCase(),
        sort: orderByParam,
        page: 1,
        limit: rowsPerPage,
        machine_ids: machineId,
        from,
        to
      })
    );
  };

  return (
    <div>
      <StyledMarginDiv marginTop="2rem">
        <StyledPaper elevation={3}>
          <HeadingWrapper expanded>
            <Typography variant="caption">{t('batch_records.alarms.title')}</Typography>
          </HeadingWrapper>
          <Table<IMetricAlarm>
            rowKey="id"
            dataSource={alarmHistoryList}
            columns={columns}
            hasFooter
            itensPerPageLabel={t('general.overview.items')}
            itemLabel={t('general.overview.of')}
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            defaultSort={{ field: sortBy, order: order as Order }}
            handleChangePage={handleChangePage}
            handleTableSort={handleTableSort}
          />
        </StyledPaper>
      </StyledMarginDiv>
    </div>
  );
};

Alarms.propTypes = {
  machineId: T.string.isRequired,
  from: T.string.isRequired,
  to: T.string.isRequired
};

export default Alarms;
